import React, { useState, useEffect, useMemo, useRef, forwardRef, useImperativeHandle, createContext, useContext } from 'react';
import { useAutoAnimate } from '@formkit/auto-animate/react';
import { toast } from 'sonner';
import { Dialog, DialogPanel, DialogTitle, Transition, TransitionChild, Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import { FieldSelectionModal } from 'app/pages/settings/sections/builder/FieldSelectionModal';
import { getFieldTypes, setSelectionCategoryNodeDefaultField } from 'services/formBuilderService';
import axiosInstance from 'utils/axios';
import { saveEntityFieldValues, getEntityFieldValues } from 'services/EntityDynamicFieldsServices';
import { useEntityDynamicFields, invalidateEntityDynamicFieldsCache } from 'hooks';
import { uploadAttachments, DocumentUploadFeature } from 'services/documentUploadService';
import { Input, Select, Textarea } from 'components/ui';
import { DatePicker } from 'components/shared/form/Datepicker';
import CustomCreatableDropdown from 'custom-components/shared/CustomCreatableDropdown';
import { AttachmentsActions } from 'components/shared/AttachmentsActions';
import RichTextEditor from 'components/shared/RichTextEditor';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import clsx from 'clsx';
import { getHtmlInputType } from 'constants/fieldType.constant';
import {
  TrashIcon,
  PlusCircleIcon,
  StarIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  ArrowsUpDownIcon,
  Bars3Icon,
  CheckIcon,
  XMarkIcon,
  ArrowUpIcon,
  ArrowDownIcon
} from '@heroicons/react/24/outline';
import { FiMove } from 'react-icons/fi';
import { useAuthContext } from 'app/contexts/auth/context';
import { EntityTypeEnum } from 'enum/EntityTypeEnum';
import { getUserPreference, saveUserPreference } from 'services/userPreferenceService';

const INPUT_CLS = 'block w-full rounded-lg border border-gray-300 dark:border-dark-600 bg-white dark:bg-dark-900 py-2.5 px-3 text-sm text-gray-900 dark:text-white focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500 placeholder:text-gray-700';

const parseDate = (str) => (str ? new Date(str + 'T00:00:00') : '');
const formatDate = (dateObj) => {
  if (!dateObj) return '';
  const y = dateObj.getFullYear();
  const m = String(dateObj.getMonth() + 1).padStart(2, '0');
  const d = String(dateObj.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

function SectionDivider({ title }) {
  return (
    <div className="relative my-4">
      <div className="absolute inset-0 flex items-center" aria-hidden="true">
        <div className="w-full border-t border-gray-200 dark:border-dark-700" />
      </div>
      <div className="relative flex justify-start">
        <span className="bg-white dark:bg-dark-800 pr-3 text-xs font-semibold text-slate-400 dark:text-slate-500">
          {title}
        </span>
      </div>
    </div>
  );
}

export const EntityDynamicFieldsContext = createContext(null);

export const useEntityDynamicFieldsContext = () => {
  const ctx = useContext(EntityDynamicFieldsContext);
  return ctx;
};

// Reorder & Position Modal for Current Step
function ReorderFieldsModal({
  isOpen,
  onClose,
  steps,
  activeStepId,
  fields,
  fieldPlacements,
  onMoveField,
  onChangeFieldPlacement,
  onRemoveField
}) {
  if (!isOpen) return null;

  const currentStepFields = fields[activeStepId] || [];
  const currentStepLabel = steps?.find((s) => s.id === activeStepId)?.label || 'Step';

  return (
    <Transition appear show={isOpen} as={React.Fragment}>
      <Dialog as="div" className="relative z-[500]" onClose={onClose}>
        <TransitionChild
          as={React.Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-900/50 dark:bg-black/60 backdrop-blur-xs" />
        </TransitionChild>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <TransitionChild
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <DialogPanel className="w-full max-w-lg rounded-2xl bg-white dark:bg-dark-800 shadow-xl overflow-hidden border border-gray-200 dark:border-dark-700">
                <div className="flex items-center justify-between border-b border-gray-200 dark:border-dark-700 px-6 py-4">
                  <div className="flex items-center gap-2">
                    <ArrowsUpDownIcon className="size-5 text-primary-600 dark:text-primary-400" />
                    <DialogTitle className="text-base font-semibold text-gray-900 dark:text-white">
                      Arrange Fields on {currentStepLabel}
                    </DialogTitle>
                  </div>
                  <button
                    onClick={onClose}
                    type="button"
                    className="size-8 flex items-center justify-center rounded-lg text-gray-500 hover:text-gray-700 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-dark-750 transition-colors"
                  >
                    <XMarkIcon className="size-5" />
                  </button>
                </div>

                <div className="p-6 max-h-[55vh] overflow-y-auto space-y-3 custom-scrollbar">
                  {currentStepFields.length === 0 ? (
                    <div className="py-8 text-center text-sm text-gray-400 dark:text-dark-400">
                      No custom fields on this step.
                    </div>
                  ) : (
                    currentStepFields.map((field, idx) => {
                      const placement = fieldPlacements[field.FieldName] || 'bottom';
                      return (
                        <div
                          key={field.Id || field.FieldName}
                          className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3.5 rounded-xl border border-gray-200 dark:border-dark-700 bg-gray-50/50 dark:bg-dark-900/30 hover:border-gray-300 dark:hover:border-dark-600 transition-all"
                        >
                          <div className="flex items-center gap-3">
                            <span className="size-6 flex items-center justify-center rounded-md bg-gray-200 dark:bg-dark-700 text-xs font-bold text-gray-700 dark:text-dark-200">
                              {idx + 1}
                            </span>
                            <div>
                              <div className="text-sm font-medium text-gray-900 dark:text-white">
                                {field.FieldLabel || field.FieldName}
                              </div>
                              <div className="text-xs text-gray-500 dark:text-dark-400 uppercase tracking-wider">
                                {field.HtmlInputType || 'text'}
                              </div>
                            </div>
                          </div>

                          <div className="flex items-center gap-2 flex-wrap">
                            {/* Section Placement Selector */}
                            <select
                              value={placement}
                              onChange={(e) => onChangeFieldPlacement(field.FieldName, e.target.value)}
                              className="text-xs py-1.5 px-2 rounded-lg border border-gray-300 dark:border-dark-600 bg-white dark:bg-dark-800 text-gray-700 dark:text-dark-200 focus:outline-none focus:ring-1 focus:ring-primary-500"
                              title="Placement on step"
                            >
                              <option value="top">Top of step</option>
                              <option value="middle">Middle of step</option>
                              <option value="bottom">Bottom of step</option>
                            </select>

                            {/* Move Up */}
                            <button
                              type="button"
                              disabled={idx === 0}
                              onClick={() => onMoveField(field.FieldName, 'up', activeStepId)}
                              className="size-7 flex items-center justify-center rounded-lg border border-gray-200 dark:border-dark-600 bg-white dark:bg-dark-800 text-gray-600 dark:text-dark-300 hover:bg-gray-100 dark:hover:bg-dark-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                              title="Move Up"
                            >
                              <ChevronUpIcon className="size-3.5" />
                            </button>

                            {/* Move Down */}
                            <button
                              type="button"
                              disabled={idx === currentStepFields.length - 1}
                              onClick={() => onMoveField(field.FieldName, 'down', activeStepId)}
                              className="size-7 flex items-center justify-center rounded-lg border border-gray-200 dark:border-dark-600 bg-white dark:bg-dark-800 text-gray-600 dark:text-dark-300 hover:bg-gray-100 dark:hover:bg-dark-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                              title="Move Down"
                            >
                              <ChevronDownIcon className="size-3.5" />
                            </button>

                            {/* Remove */}
                            <button
                              type="button"
                              onClick={() => onRemoveField(field.FieldName)}
                              className="size-7 flex items-center justify-center rounded-lg border border-red-200 dark:border-red-900/40 bg-red-50 dark:bg-red-950/20 text-red-600 dark:text-red-400 hover:bg-red-100 transition-colors"
                              title="Remove Field"
                            >
                              <TrashIcon className="size-3.5" />
                            </button>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>

                <div className="flex justify-end border-t border-gray-200 dark:border-dark-700 px-6 py-3.5 bg-gray-50/50 dark:bg-dark-900/50">
                  <button
                    type="button"
                    onClick={onClose}
                    className="px-4 py-2 text-xs font-semibold text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors shadow-xs"
                  >
                    Done
                  </button>
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}

// Single Field Item Renderer
function RenderDynamicFieldItem({
  field,
  index,
  totalOnStep,
  stepId,
  dynamicFieldValues,
  handleDynamicFieldChange,
  handleRemoveField,
  handleMoveField,
  handleMoveFieldByIndex,
  dragFromIndex,
  dragOverIndex,
  dragPosition,
  fieldDraggable,
  handleMouseDown,
  handleDragStart,
  handleDragOver,
  handleDrop,
  handleDragEnd,
  isHovered,
  setHoveredIndex,
  isSelectionModule,
  // Whether the "star" (set-as-default-for-this-Main-Category) control is shown at all. Defaults
  // to true -- Selection Management (AddSelectionModal, editing a master SelectionOption) is where
  // that privilege belongs. Job Selections' Pending->Confirm flow (ConfirmLocationModal) passes
  // false: confirming a job's option edits THIS job's own field values only and must never expose
  // a control that changes what auto-adds as default for every future option under this category.
  allowSetDefault = true,
  findFieldInMasterFields,
  handleToggleFieldDefault,
  togglingDefaultFieldName,
  autoAddAttachments,
  loggedInUserName,
  isDragAndDropEnabled = false
}) {
  const value = dynamicFieldValues[field.FieldName] ?? '';
  const fieldType = (
    field.HtmlInputType ||
    field.htmlInputType ||
    (field.FieldTypeId || field.fieldTypeId ? getHtmlInputType(field.FieldTypeId || field.fieldTypeId) : '') ||
    field.type ||
    'text'
  ).toLowerCase();

  const isFullWidthField =
    fieldType === 'editor' ||
    fieldType === 'texteditor' ||
    fieldType === 'richtext' ||
    fieldType === 'rich-text' ||
    Number(field.FieldTypeId || field.fieldTypeId) === 11 ||
    Boolean(field.fullWidth || field.IsFullWidth || field.isFullWidth);

  const isAutoAddedAttachment = autoAddAttachments && (field.FieldName || '').toLowerCase() === 'attachments';
  const masterFieldMatch = findFieldInMasterFields ? findFieldInMasterFields(field.FieldName, field.masterFieldId || field.Id) : null;
  const isDefaultField = !!(masterFieldMatch?.field?.IsSelectionDefaultForCategoryNode || masterFieldMatch?.field?.isSelectionDefaultForCategoryNode);

  const isDragging = isDragAndDropEnabled && dragFromIndex === index;
  const isTargetOver = isDragAndDropEnabled && dragOverIndex === index && dragFromIndex !== null && dragFromIndex !== index;

  const starButton = (isSelectionModule && allowSetDefault && !isAutoAddedAttachment && masterFieldMatch) ? (
    <button
      type="button"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        handleToggleFieldDefault(field);
      }}
      disabled={togglingDefaultFieldName === field.FieldName}
      className={clsx(
        "size-7 flex items-center justify-center rounded-lg border shadow-xs hover:shadow-sm transition-all cursor-pointer z-10 disabled:opacity-50 disabled:cursor-not-allowed",
        isDefaultField
          ? "bg-amber-50 dark:bg-amber-950/40 border-amber-300 dark:border-amber-700 text-amber-500 hover:bg-amber-100 dark:hover:bg-amber-950/60"
          : "bg-white dark:bg-dark-800 border-gray-200 dark:border-dark-600 text-gray-400 hover:text-amber-500 hover:border-amber-200 dark:hover:border-amber-800"
      )}
      title={isDefaultField ? `Default field — click to remove "${field.FieldLabel}"` : `Set as default`}
    >
      <StarIcon className={clsx("size-3.5", isDefaultField && "fill-amber-400 text-amber-500")} />
    </button>
  ) : null;

  // Move Earlier / Left (matches Form Builder onMoveField) - Vehicle Register only
  const moveUpBtn = isDragAndDropEnabled && typeof index === 'number' && index > 0 ? (
    <button
      type="button"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        if (handleMoveFieldByIndex) {
          handleMoveFieldByIndex(stepId, index, index - 1);
        } else if (handleMoveField) {
          handleMoveField(field.FieldName, 'up', stepId);
        }
      }}
      className="size-7 bg-white dark:bg-dark-800 hover:bg-gray-100 dark:hover:bg-dark-700 text-gray-600 hover:text-gray-900 dark:text-dark-300 shadow-xs hover:shadow-sm transition-all rounded-lg border border-gray-200 dark:border-dark-600 flex items-center justify-center cursor-pointer"
      title="Move earlier"
    >
      <ArrowUpIcon className="size-3.5 stroke-2" />
    </button>
  ) : null;

  // Move Later / Right (matches Form Builder onMoveField) - Vehicle Register only
  const moveDownBtn = isDragAndDropEnabled && typeof index === 'number' && typeof totalOnStep === 'number' && index < totalOnStep - 1 ? (
    <button
      type="button"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        if (handleMoveFieldByIndex) {
          handleMoveFieldByIndex(stepId, index, index + 1);
        } else if (handleMoveField) {
          handleMoveField(field.FieldName, 'down', stepId);
        }
      }}
      className="size-7 bg-white dark:bg-dark-800 hover:bg-gray-100 dark:hover:bg-dark-700 text-gray-600 hover:text-gray-900 dark:text-dark-300 shadow-xs hover:shadow-sm transition-all rounded-lg border border-gray-200 dark:border-dark-600 flex items-center justify-center cursor-pointer"
      title="Move later"
    >
      <ArrowDownIcon className="size-3.5 stroke-2" />
    </button>
  ) : null;

  // Drag Handle with FiMove - Vehicle Register only
  const dragHandle = isDragAndDropEnabled ? (
    <div
      className="size-7 bg-white dark:bg-dark-800 text-gray-500 hover:text-primary-600 dark:text-dark-300 shadow-xs rounded-lg border border-gray-200 dark:border-dark-600 flex items-center justify-center cursor-grab active:cursor-grabbing select-none"
      title="Drag to reorder"
    >
      <FiMove className="size-3.5 pointer-events-none" />
    </div>
  ) : null;

  // Remove Button
  const removeButton = !isAutoAddedAttachment ? (
    <button
      type="button"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        handleRemoveField(field.FieldName);
      }}
      className="size-7 flex items-center justify-center bg-white dark:bg-dark-800 hover:bg-red-50 dark:hover:bg-red-950/40 border border-red-200 dark:border-red-900/40 text-red-500 hover:text-red-650 dark:text-red-400 rounded-lg shadow-xs hover:shadow-sm transition-all cursor-pointer z-10"
      title={`Remove ${field.FieldLabel}`}
    >
      <TrashIcon className="size-3.5" />
    </button>
  ) : null;

  const renderCardWrapper = (content, customId = undefined) => (
    <div
      id={customId}
      key={field.Id || field.FieldName}
      draggable={isDragAndDropEnabled ? fieldDraggable : undefined}
      onMouseDown={isDragAndDropEnabled ? handleMouseDown : undefined}
      onDragStart={isDragAndDropEnabled ? (e) => handleDragStart?.(index, e) : undefined}
      onDragEnd={isDragAndDropEnabled ? handleDragEnd : undefined}
      onDragOver={isDragAndDropEnabled ? (e) => handleDragOver?.(index, e) : undefined}
      onDrop={isDragAndDropEnabled ? (e) => handleDrop?.(index, e) : undefined}
      onMouseEnter={isDragAndDropEnabled ? () => setHoveredIndex?.(index) : undefined}
      onMouseLeave={isDragAndDropEnabled ? () => setHoveredIndex?.(null) : undefined}
      className={clsx(
        isFullWidthField ? "col-span-1 sm:col-span-2" : "col-span-1",
        "relative pt-4 pb-3.5 px-3.5 border rounded-xl transition-all duration-200 group bg-white dark:bg-dark-850",
        isDragAndDropEnabled && "cursor-move",
        isDragging
          ? "opacity-35 scale-[0.98] border-2 border-dashed border-primary-500 bg-primary-50/20 shadow-none"
          : (isDragAndDropEnabled && isHovered)
          ? "border-amber-400 dark:border-amber-500 shadow-md ring-1 ring-amber-400/30"
          : "border-gray-200 dark:border-dark-700 shadow-2xs hover:border-gray-300 dark:hover:border-dark-600"
      )}
    >
      {/* Drop Indicator Line (matches Form Builder InteractiveFormPreview) */}
      {isDragAndDropEnabled && isTargetOver && (
        <div
          className={clsx(
            "absolute inset-x-0 h-1.5 bg-primary-600 rounded-full z-30 shadow-md animate-pulse pointer-events-none",
            dragPosition === 'before' ? "-top-1.5" : "-bottom-1.5"
          )}
        />
      )}

      {/* Action Toolbar on top-right */}
      <div
        className={clsx(
          "absolute -top-3 right-3 flex items-center gap-1 z-20 transition-opacity duration-150",
          isDragAndDropEnabled
            ? (isHovered ? "opacity-100" : "opacity-85 sm:opacity-95")
            : "opacity-100"
        )}
      >
        {dragHandle}
        {moveUpBtn}
        {moveDownBtn}
        {starButton}
        {removeButton}
      </div>

      {content}
    </div>
  );

  if (fieldType === 'textarea') {
    return renderCardWrapper(
      <Textarea
        label={field.FieldLabel}
        required={field.IsMendatory}
        rows={3}
        value={value}
        onChange={(e) => handleDynamicFieldChange(field.FieldName, e.target.value)}
        placeholder={field.Placeholder || ""}
      />
    );
  }

  if (fieldType === 'select' || fieldType === 'dropdown') {
    const opts = (field.MasterFormFieldOptions || field.options || []).map((o) => ({
      label: o.OptionLabel || o.optionLabel || o.label || "",
      value: o.OptionValue || o.optionValue || o.value || "",
    }));
    const selectedVal = opts.find((o) => String(o.value) === String(value)) || null;

    return renderCardWrapper(
      <>
        <label className="block text-xs font-semibold text-gray-700 dark:text-dark-300 mb-1.5">
          {field.FieldLabel} {field.IsMendatory && <span className="text-red-500">*</span>}
        </label>
        <CustomCreatableDropdown
          options={opts}
          value={selectedVal}
          onChange={(newValue) => {
            handleDynamicFieldChange(field.FieldName, newValue ? newValue.value : "");
          }}
          placeholder={field.Placeholder || `Select ${field.FieldLabel}`}
          disabled={false}
          error={false}
        />
      </>
    );
  }

  if (fieldType === 'checkbox') {
    const checkOptions = field.options || field.MasterFormFieldOptions || [];
    if (checkOptions.length === 0) {
      return renderCardWrapper(
        <div className="flex items-center mt-2">
          <input
            type="checkbox"
            checked={value === true || value === "true" || value === "1"}
            onChange={(e) => handleDynamicFieldChange(field.FieldName, e.target.checked)}
            className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
          />
          <label className="ml-2 block text-sm text-gray-900 dark:text-gray-100 font-medium">
            {field.FieldLabel} {field.IsMendatory && <span className="text-error">*</span>}
          </label>
        </div>
      );
    }
    const checkValue = Array.isArray(value) ? value : (typeof value === 'string' ? value.split(',').filter(Boolean) : []);
    return renderCardWrapper(
      <>
        <label className="block text-xs font-semibold text-gray-700 dark:text-dark-300 mb-1.5">
          {field.FieldLabel} {field.IsMendatory && <span className="text-red-500">*</span>}
        </label>
        <div className="flex flex-wrap gap-4 mt-2">
          {checkOptions.map((option, idx) => {
            const optionValue = option.OptionValue || option.optionValue;
            const optionLabel = option.OptionLabel || option.optionLabel;
            const isChecked = checkValue.includes(optionValue);

            return (
              <label key={idx} className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={isChecked}
                  onChange={(e) => {
                    let newValues = [...checkValue];
                    if (e.target.checked) {
                      newValues.push(optionValue);
                    } else {
                      newValues = newValues.filter((v) => v !== optionValue);
                    }
                    handleDynamicFieldChange(field.FieldName, newValues.join(','));
                  }}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded mr-2"
                />
                <span className="text-sm text-gray-755 dark:text-dark-200">{optionLabel}</span>
              </label>
            );
          })}
        </div>
      </>
    );
  }

  if (fieldType === 'radio') {
    const radioOptions = field.options || field.MasterFormFieldOptions || [];
    return renderCardWrapper(
      <>
        <label className="block text-xs font-semibold text-gray-700 dark:text-dark-300 mb-1.5">
          {field.FieldLabel} {field.IsMendatory && <span className="text-red-500">*</span>}
        </label>
        <div className="flex flex-wrap gap-4 mt-2">
          {radioOptions.map((option, idx) => {
            const optionValue = option.OptionValue || option.optionValue;
            const optionLabel = option.OptionLabel || option.optionLabel;
            const isSelected = String(value) === String(optionValue);

            return (
              <label key={idx} className="flex items-center cursor-pointer">
                <input
                  type="radio"
                  name={field.FieldName}
                  value={optionValue}
                  checked={isSelected}
                  onChange={(e) => handleDynamicFieldChange(field.FieldName, e.target.value)}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 mr-2"
                />
                <span className="text-sm text-gray-755 dark:text-dark-200">{optionLabel}</span>
              </label>
            );
          })}
        </div>
      </>
    );
  }

  if (fieldType === 'date') {
    return renderCardWrapper(
      <DatePicker
        label={field.FieldLabel}
        required={field.IsMendatory}
        value={parseDate(value)}
        onChange={([d]) => handleDynamicFieldChange(field.FieldName, formatDate(d))}
        placeholder="Choose date"
        options={{
          dateFormat: "d/m/Y",
          altFormat: "d/m/Y",
          disableMobile: true,
        }}
      />
    );
  }

  if (
    fieldType === 'editor' ||
    fieldType === 'texteditor' ||
    fieldType === 'richtext' ||
    fieldType === 'rich-text' ||
    Number(field.FieldTypeId || field.fieldTypeId) === 11
  ) {
    return renderCardWrapper(
      <>
        <label className="block text-xs font-semibold text-gray-700 dark:text-dark-300 mb-1.5">
          {field.FieldLabel} {field.IsMendatory && <span className="text-red-500">*</span>}
        </label>
        <RichTextEditor
          value={value}
          onChange={(val) => handleDynamicFieldChange(field.FieldName, val)}
          placeholder={field.Placeholder || "Enter rich text content..."}
          height="180px"
        />
      </>
    );
  }

  if (fieldType === 'attachments' || fieldType === 'file') {
    let currentFiles = value || [];
    if (typeof currentFiles === 'string' && currentFiles.trim()) {
      try {
        const parsed = JSON.parse(currentFiles);
        if (Array.isArray(parsed)) {
          currentFiles = parsed.map((doc) => ({
            name: doc.fileName || doc.name || doc.filePath?.split('/').pop() || 'Attachment',
            size: (doc.fileSizeKB || 0) * 1024,
            filePath: doc.filePath || doc.path || doc.FileUrl,
            path: doc.filePath || doc.path || doc.FileUrl,
            fileExtension: doc.fileExtension,
            fileSizeKB: doc.fileSizeKB,
            uploadedAt: doc.uploadedAt || doc.UploadedAt || doc.createdAt || doc.CreatedAt,
            uploadedBy: doc.uploadedBy || doc.UploadedBy || doc.uploadedByName || doc.UploadedByName || loggedInUserName,
            uploadedByName: doc.uploadedByName || doc.UploadedByName || doc.uploadedBy || doc.UploadedBy || loggedInUserName,
            UploadedByName: doc.UploadedByName || doc.uploadedByName || doc.uploadedBy || doc.UploadedBy || loggedInUserName,
            isExisting: true
          }));
        } else {
          currentFiles = [];
        }
      } catch (e) {
        currentFiles = currentFiles.split(',').map((f) => {
          const path = f.trim();
          return {
            name: path.split('/').pop() || path,
            size: 0,
            filePath: path,
            path: path,
            isExisting: true
          };
        }).filter((f) => f.path);
      }
    } else if (!Array.isArray(currentFiles)) {
      currentFiles = [];
    }

    return renderCardWrapper(
      <>
        <label className="block text-xs font-semibold text-gray-700 dark:text-dark-300 mb-1.5">
          {field.FieldLabel} {field.IsMendatory && <span className="text-red-500">*</span>}
        </label>
        <AttachmentsActions
          files={currentFiles}
          onChange={(files) => {
            handleDynamicFieldChange(field.FieldName, files);
            setTimeout(() => {
              const el = document.getElementById(`dynamic-field-${field.Id}`);
              if (el) {
                el.scrollIntoView({ behavior: 'smooth', block: 'end' });
              }
            }, 100);
          }}
          addLabel={field.FieldLabel || "Upload Files"}
          showLabel={false}
        />
      </>,
      `dynamic-field-${field.Id}`
    );
  }

  if (fieldType === 'phone' || fieldType === 'tel') {
    return renderCardWrapper(
      <>
        <label className="block text-xs font-semibold text-gray-700 dark:text-dark-300 mb-1.5">
          {field.FieldLabel} {field.IsMendatory && <span className="text-red-500">*</span>}
        </label>
        <PhoneInput
          countryCallingCodeEditable={false}
          international
          defaultCountry="AU"
          value={value}
          onChange={(val) => handleDynamicFieldChange(field.FieldName, val || '')}
          className={clsx(
            "w-full rounded-lg border border-gray-300 dark:border-dark-600 bg-white dark:bg-dark-900 py-2.5 px-3 text-sm text-gray-900 dark:text-white focus-within:border-primary-500 focus-within:ring-1 focus-within:ring-primary-500"
          )}
          numberInputProps={{
            className: "w-full bg-transparent border-none focus:ring-0 p-0 text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none"
          }}
        />
      </>
    );
  }

  return renderCardWrapper(
    <Input
      className={INPUT_CLS}
      label={field.FieldLabel}
      required={field.IsMendatory}
      type={fieldType === 'number' ? 'number' : 'text'}
      value={value}
      onChange={(e) => handleDynamicFieldChange(field.FieldName, e.target.value)}
      placeholder={field.Placeholder || ""}
    />
  );
}

export const EntityDynamicFieldsProvider = forwardRef(({
  entityTypeCode,
  entityId,
  companyId,
  userId,
  sectionTitle = "Other Info",
  onFieldAdded,
  autoAddAttachments,
  isSelectionModule = false,
  // Whether the star (set-as-default-for-this-Main-Category) control is available at all --
  // see RenderDynamicFieldItem's comment. True for Selection Management (master option editing);
  // Job Selections' Pending->Confirm flow passes false.
  allowSetDefault = true,
  // Main Category (Level 1 SelectionCategoryNode) the Selection Option being added/edited
  // belongs to -- scopes which fields auto-add as "default" to that specific category instead
  // of the old company-wide default set. Only meaningful when isSelectionModule is true.
  categoryNodeId = null,
  preloadedFieldNames = [],
  steps = null,
  currentStep = null,
  preferencePageKey = null,
  preferenceTypeKey = "CustomFieldsLayout",
  enableDragAndDrop,
  children
}, ref) => {
  const isEdit = !!entityId;
  const { user: authUser } = useAuthContext();
  const loggedInUserName = authUser?.FullName || authUser?.fullName || authUser?.name || authUser?.unique_name || 'SunnyHomes Admin';

  const isVehicleRegister =
    entityTypeCode === EntityTypeEnum.Vehicle ||
    entityTypeCode === 3 ||
    entityTypeCode === '3' ||
    String(entityTypeCode || '').toLowerCase() === 'vehicle' ||
    preferencePageKey === 'whs_vehicle_register';

  const isDragAndDropEnabled =
    typeof enableDragAndDrop === 'boolean' ? enableDragAndDrop : isVehicleRegister;

  const [isAddFieldModalOpen, setIsAddFieldModalOpen] = useState(false);
  const [targetStepForAdd, setTargetStepForAdd] = useState(null);
  const [isReorderModalOpen, setIsReorderModalOpen] = useState(false);
  const [reorderActiveStep, setReorderActiveStep] = useState(null);

  const [allMasterFields, setAllMasterFields] = useState([]);
  const [masterFieldsLoading, setMasterFieldsLoading] = useState(false);
  const [masterFieldsError, setMasterFieldsError] = useState(null);
  const [fieldTypes, setFieldTypes] = useState([]);
  const [addedFields, setAddedFields] = useState([]);
  const [dynamicFieldValues, setDynamicFieldValues] = useState({});
  const [togglingDefaultFieldName, setTogglingDefaultFieldName] = useState(null);

  // Layout Preference State: { [stepId]: string[] } (array of field names)
  const [stepLayout, setStepLayout] = useState({});
  // Placement within step: { [fieldName]: 'top' | 'middle' | 'bottom' }
  const [fieldPlacements, setFieldPlacements] = useState({});
  const [isLayoutLoaded, setIsLayoutLoaded] = useState(!preferencePageKey);

  const { fieldValues: apiFieldValues, dynamicFieldsData } = useEntityDynamicFields(
    entityTypeCode,
    entityId || null
  );

  // Load layout from Tenant Preferences
  useEffect(() => {
    let isCancelled = false;
    const fetchLayoutPreference = async () => {
      if (!preferencePageKey) {
        setIsLayoutLoaded(true);
        return;
      }
      try {
        const saved = await getUserPreference(preferencePageKey, preferenceTypeKey);
        if (!isCancelled) {
          if (saved && typeof saved === 'object') {
            const layout = saved.stepLayout || saved;
            if (layout && typeof layout === 'object') {
              setStepLayout(layout);
            }
            if (saved.fieldPlacements && typeof saved.fieldPlacements === 'object') {
              setFieldPlacements(saved.fieldPlacements);
            }
          }
        }
      } catch (err) {
        console.error('Error fetching dynamic fields layout preference:', err);
      } finally {
        if (!isCancelled) {
          setIsLayoutLoaded(true);
        }
      }
    };

    fetchLayoutPreference();
    return () => {
      isCancelled = true;
    };
  }, [preferencePageKey, preferenceTypeKey]);

  // Persist updated layout to Tenant Preferences
  const persistLayoutPreference = async (newLayout, newPlacements = fieldPlacements) => {
    setStepLayout(newLayout);
    setFieldPlacements(newPlacements);
    if (!preferencePageKey) return;
    try {
      await saveUserPreference(
        preferencePageKey,
        { stepLayout: newLayout, fieldPlacements: newPlacements },
        preferenceTypeKey
      );
    } catch (err) {
      console.error('Error saving dynamic fields layout preference:', err);
    }
  };

  const findFieldInMasterFields = (fieldName, masterFieldId) => {
    for (const category of allMasterFields) {
      const field = category.MasterFields?.find(
        (f) =>
          (fieldName && f.FieldName?.toLowerCase() === fieldName?.toLowerCase()) ||
          (masterFieldId && f.Id === masterFieldId)
      );
      if (field) {
        return { field, category };
      }
    }
    return null;
  };

  // Top-level (not scoped inside a single effect) so both the initial-load effect and the
  // Main-Category-switch reconciliation effect below can call it.
  const autoAddSelectionDefaultFields = (fields, values) => {
    if (!isSelectionModule || !allMasterFields || allMasterFields.length === 0) {
      return { newFields: fields, newValues: values };
    }
    let newFields = [...fields];
    let newValues = { ...values };
    allMasterFields.forEach((category) => {
      (category.MasterFields || []).forEach((fieldData) => {
        // Default-ness is scoped per Main Category now (IsSelectionDefaultForCategoryNode,
        // computed server-side from categoryNodeId) -- NOT the old company-wide
        // SelectionModuleDefault bit, which would bleed the same default set into every
        // category. When categoryNodeId hasn't been chosen yet, the backend returns this as
        // false for every field, so nothing auto-adds until a Main Category is picked.
        const isDefault = fieldData.IsSelectionDefaultForCategoryNode || fieldData.isSelectionDefaultForCategoryNode;
        if (!isDefault) return;
        const fieldName = fieldData.FieldName || fieldData.fieldName;
        const alreadyAdded = newFields.some((f) => (f.FieldName || '').toLowerCase() === (fieldName || '').toLowerCase());
        if (alreadyAdded) return;
        const newField = {
          Id: fieldData.Id || `master-${Date.now()}-${fieldName}`,
          masterFieldId: fieldData.Id,
          FieldName: fieldName,
          FieldLabel: fieldData.FieldLabel || fieldData.fieldLabel,
          HtmlInputType: fieldData.HtmlInputType || fieldData.htmlInputType || getHtmlInputType(fieldData.FieldTypeId || fieldData.fieldTypeId) || 'text',
          IsMendatory: fieldData.IsMendatory || fieldData.isMendatory || false,
          options: fieldData.MasterFormFieldOptions || fieldData.options || [],
          DefaultValue: fieldData.DefaultValue || fieldData.defaultValue || '',
          Placeholder: fieldData.Placeholder || fieldData.placeholder || '',
          // Marks this as added BY the per-category default logic (not by the user via "Add
          // Fields") so the Main-Category-switch reconciliation effect below knows it's safe to
          // drop if it stops being default for whatever category is selected next.
          IsAutoDefaultField: true,
        };
        newFields.push(newField);
        newValues[newField.FieldName] = newField.DefaultValue || '';
      });
    });
    return { newFields, newValues };
  };

  const fetchAllMasterFields = async () => {
    setMasterFieldsLoading(true);
    setMasterFieldsError(null);
    try {
      const params = {
        ...(companyId ? { companyId } : {}),
        isSelectionModule: isSelectionModule ?? false,
        ...(isSelectionModule && categoryNodeId ? { categoryNodeId } : {}),
      };
      const response = await axiosInstance.get('/api/FormDesk/GetMasterFields', { params });
      if (response.data?.status === "Success" && response.data?.data) {
        const parsedData = JSON.parse(response.data.data);
        setAllMasterFields(parsedData);
      } else {
        setMasterFieldsError(response.data?.message || 'Failed to fetch master fields');
      }
    } catch (error) {
      console.error('Error fetching master fields:', error);
      setMasterFieldsError('Failed to load master fields');
    } finally {
      setMasterFieldsLoading(false);
    }
  };

  const fetchFieldTypes = async () => {
    try {
      const result = await getFieldTypes(companyId, isSelectionModule ? true : null);
      if (result.success && result.data) {
        setFieldTypes(result.data);
      }
    } catch (error) {
      console.error('Error fetching field types:', error);
    }
  };

  useEffect(() => {
    fetchAllMasterFields();
    fetchFieldTypes();
  }, [companyId, isSelectionModule, categoryNodeId]);

  useEffect(() => {
    if (!isAddFieldModalOpen) return;
    fetchAllMasterFields();
    fetchFieldTypes();
  }, [isAddFieldModalOpen]);

  const [isInitialized, setIsInitialized] = useState(false);

  // Local storage fallback.
  // When isSelectionModule and a specific categoryNodeId are both present, scope the key to
  // that node so each Main Category gets its own remembered field list. Without this, a field
  // manually added for "External" would re-appear when opening any other category ("Internal",
  // "Tiles Selections", "Bricks", etc.) because they all share the same entity-type key.
  const getSavedFieldsKey = () =>
    isSelectionModule && categoryNodeId
      ? `dynamic_fields_pref_${entityTypeCode}_${categoryNodeId}`
      : `dynamic_fields_pref_${entityTypeCode}`;
  const getSavedFields = () => {
    try {
      const saved = localStorage.getItem(getSavedFieldsKey());
      return saved ? JSON.parse(saved) : [];
    } catch (e) {
      return [];
    }
  };

  const saveFieldToStorage = (fieldName) => {
    try {
      const saved = getSavedFields();
      if (!saved.includes(fieldName)) {
        saved.push(fieldName);
        localStorage.setItem(getSavedFieldsKey(), JSON.stringify(saved));
      }
    } catch (e) {
      console.error(e);
    }
  };

  const removeFieldFromStorage = (fieldName) => {
    try {
      let saved = getSavedFields();
      saved = saved.filter((f) => f !== fieldName);
      localStorage.setItem(getSavedFieldsKey(), JSON.stringify(saved));
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    setIsInitialized(false);
    setAddedFields([]);
    setDynamicFieldValues({});
  }, [entityId]);

  useEffect(() => {
    if (!isLayoutLoaded) return;

    const dataEntityId = dynamicFieldsData?.EntityId || dynamicFieldsData?.entityId;
    const isDataMatching = !entityId || (dataEntityId && String(dataEntityId).toLowerCase() === String(entityId).toLowerCase());

    const autoAddAttachmentField = (fields, values) => {
      let newFields = [...fields];
      let newValues = { ...values };
      if (autoAddAttachments && allMasterFields && allMasterFields.length > 0) {
        const hasAttachments = newFields.some((f) => (f.FieldName || '').toLowerCase() === 'attachments');
        if (!hasAttachments) {
          const found = findFieldInMasterFields('attachments');
          if (found) {
            const fieldData = found.field;
            const newField = {
              Id: fieldData.masterFieldId || fieldData.Id || `master-${Date.now()}`,
              masterFieldId: fieldData.masterFieldId || fieldData.Id,
              FieldName: fieldData.fieldName || fieldData.FieldName,
              FieldLabel: fieldData.fieldLabel || fieldData.FieldLabel,
              HtmlInputType: (() => {
        const rawType = (
          fieldData.htmlInputType ||
          fieldData.HtmlInputType ||
          fieldData.value ||
          (fieldData.fieldTypeId || fieldData.FieldTypeId ? getHtmlInputType(fieldData.fieldTypeId || fieldData.FieldTypeId) : '') ||
          'text'
        ).toLowerCase();
        return (rawType === 'texteditor' || rawType === 'editor' || Number(fieldData.fieldTypeId || fieldData.FieldTypeId) === 11)
          ? 'editor'
          : rawType;
      })(),
              IsMendatory: fieldData.isMendatory || fieldData.IsMendatory || false,
              options: fieldData.options || fieldData.MasterFormFieldOptions || [],
              DefaultValue: fieldData.defaultValue || fieldData.DefaultValue || '',
              Placeholder: fieldData.placeholder || fieldData.Placeholder || '',
            };
            newFields.push(newField);
            newValues[newField.FieldName] = newField.DefaultValue || '';
          }
        }
      }
      return { newFields, newValues };
    };

    if (isEdit && apiFieldValues && isDataMatching && !isInitialized) {
      const initialFields = apiFieldValues.map((fv) => ({
        Id: fv.MasterFieldId || fv.masterFieldId,
        masterFieldId: fv.MasterFieldId || fv.masterFieldId,
        FieldName: fv.FieldName || fv.fieldName,
        FieldLabel: fv.FieldName || fv.fieldName,
        HtmlInputType: 'text',
        options: []
      }));

      const initialValues = {};
      apiFieldValues.forEach((fv) => {
        const fieldName = fv.FieldName || fv.fieldName;
        initialValues[fieldName] = fv.FieldValue || fv.fieldValue || '';
      });

      const layoutFieldNames = [];
      Object.values(stepLayout || {}).forEach((arr) => {
        if (Array.isArray(arr)) arr.forEach((n) => { if (n && !layoutFieldNames.includes(n)) layoutFieldNames.push(n); });
      });

      const savedFieldsFromStorage = getSavedFieldsKey ? getSavedFields() : [];
      const fieldsToLoad = Array.from(new Set([...preloadedFieldNames, ...layoutFieldNames, ...savedFieldsFromStorage]));

      fieldsToLoad.forEach((fieldName) => {
        if (!initialFields.some((f) => (f.FieldName || '').toLowerCase() === (fieldName || '').toLowerCase())) {
          const found = findFieldInMasterFields(fieldName);
          if (found) {
            const fieldData = found.field;
            // When this form is scoped to a specific Selection Main Category, only pull in
            // extra fields (from localStorage / layout preferences) that are actually marked as
            // default for THAT category (IsSelectionDefaultForCategoryNode). Without this guard
            // a field starred for "External" would bleed into the confirm modal for "Tiles
            // Selections" because localStorage is global across all categories.
            // Fields that already have a saved value (from apiFieldValues above) are always
            // shown regardless -- this path only handles the extras that have no saved value.
            if (isSelectionModule && categoryNodeId) {
              const isDefaultForThisCategory =
                fieldData.IsSelectionDefaultForCategoryNode ||
                fieldData.isSelectionDefaultForCategoryNode;
              if (!isDefaultForThisCategory) return;
            }
            initialFields.push({
              Id: fieldData.masterFieldId || fieldData.Id || `master-${Date.now()}-${fieldName}`,
              masterFieldId: fieldData.masterFieldId || fieldData.Id,
              FieldName: fieldData.fieldName || fieldData.FieldName,
              FieldLabel: fieldData.fieldLabel || fieldData.FieldLabel,
              HtmlInputType: 'text',
              options: [],
              IsAutoDefaultField: true,
            });
            initialValues[fieldData.fieldName || fieldData.FieldName] = fieldData.defaultValue || fieldData.DefaultValue || '';
          }
        }
      });

      if (allMasterFields && allMasterFields.length > 0) {
        const enrichedFields = initialFields.map((field) => {
          const found = findFieldInMasterFields(field.FieldName, field.masterFieldId);
          if (found) {
            return {
              ...field,
              FieldLabel: found.field.FieldLabel,
              HtmlInputType: found.field.HtmlInputType || found.field.htmlInputType || getHtmlInputType(found.field.FieldTypeId || found.field.fieldTypeId) || 'text',
              options: found.field.MasterFormFieldOptions || found.field.options || []
            };
          }
          return field;
        });

        const { newFields, newValues } = autoAddAttachmentField(enrichedFields, initialValues);
        setAddedFields(newFields);
        setDynamicFieldValues(newValues);
        setIsInitialized(true);
      } else {
        setAddedFields(initialFields);
        setDynamicFieldValues(initialValues);
      }
    } else if (!isEdit && !isInitialized && allMasterFields && allMasterFields.length > 0) {
      let initialFields = [];
      let initialValues = {};

      const layoutFieldNames = [];
      Object.values(stepLayout || {}).forEach((arr) => {
        if (Array.isArray(arr)) arr.forEach((n) => { if (n && !layoutFieldNames.includes(n)) layoutFieldNames.push(n); });
      });

      const savedFieldsFromStorage = getSavedFieldsKey ? getSavedFields() : [];
      const fieldsToLoad = Array.from(new Set([...preloadedFieldNames, ...layoutFieldNames, ...savedFieldsFromStorage]));

      if (fieldsToLoad && fieldsToLoad.length > 0) {
        fieldsToLoad.forEach((fieldName) => {
          const found = findFieldInMasterFields(fieldName);
          if (found) {
            const fieldData = found.field;
            // Same guard as the edit-mode path: when this form is scoped to a specific
            // Selection Main Category, only restore fields from localStorage / layout
            // preferences that are actually marked as default for THAT category. A field
            // that was manually added (and saved to localStorage) while working under
            // "External" must not appear automatically when the user opens "Add Option"
            // under "Tiles Selections" or any other Main Category.
            if (isSelectionModule && categoryNodeId) {
              const isDefaultForThisCategory =
                fieldData.IsSelectionDefaultForCategoryNode ||
                fieldData.isSelectionDefaultForCategoryNode;
              if (!isDefaultForThisCategory) return;
            }
            const newField = {
              Id: fieldData.masterFieldId || fieldData.Id || `master-${Date.now()}-${fieldName}`,
              masterFieldId: fieldData.masterFieldId || fieldData.Id,
              FieldName: fieldData.fieldName || fieldData.FieldName,
              FieldLabel: fieldData.fieldLabel || fieldData.FieldLabel,
              HtmlInputType: fieldData.htmlInputType || fieldData.HtmlInputType || getHtmlInputType(fieldData.fieldTypeId || fieldData.FieldTypeId) || 'text',
              IsMendatory: fieldData.isMendatory || fieldData.IsMendatory || false,
              options: fieldData.options || fieldData.MasterFormFieldOptions || [],
              DefaultValue: fieldData.defaultValue || fieldData.DefaultValue || '',
              Placeholder: fieldData.placeholder || fieldData.Placeholder || '',
              // Marks this as pulled in by the per-category default logic (not by the user
              // via "Add Fields") so the reconciliation effect knows it's safe to drop it
              // if it stops being default for whatever category is selected next.
              IsAutoDefaultField: true,
            };
            if (!initialFields.some((f) => f.FieldName === newField.FieldName)) {
              initialFields.push(newField);
              initialValues[newField.FieldName] = newField.DefaultValue || '';
            }
          }
        });
      }

      const afterAttachments = autoAddAttachmentField(initialFields, initialValues);
      const { newFields, newValues } = autoAddSelectionDefaultFields(afterAttachments.newFields, afterAttachments.newValues);
      setAddedFields(newFields);
      setDynamicFieldValues(newValues);
      setIsInitialized(true);
    }
  }, [apiFieldValues, dynamicFieldsData, allMasterFields, isEdit, isInitialized, entityId, autoAddAttachments, preloadedFieldNames, isLayoutLoaded, stepLayout]);

  // Kept in sync so the reconciliation effect below can read the latest typed-in values without
  // needing dynamicFieldValues in its own dependency array (which would re-run it on every
  // keystroke instead of only when the Main Category / its default set actually changes).
  const latestFieldValuesRef = useRef({});
  useEffect(() => {
    latestFieldValuesRef.current = dynamicFieldValues;
  }, [dynamicFieldValues]);

  // Tracks the Main Category this effect last reconciled against, so it can tell "the category
  // actually changed" apart from "allMasterFields just refetched" (e.g. after a star-toggle) --
  // see the categoryChanged check below.
  const prevCategoryNodeIdRef = useRef(categoryNodeId);

  // Re-sync auto-added default fields when the Main Category changes mid-add (new option, not
  // yet saved): a field that was only in the Properties section because it was default for the
  // PREVIOUS Main Category must not linger once it's no longer default for the one now selected.
  // Only fields the default logic itself added (IsAutoDefaultField) are ever touched here, and
  // only when still empty -- a field the user manually added or already typed a value into is
  // never auto-removed. Then whatever's newly default for the new category gets added in.
  useEffect(() => {
    if (!isSelectionModule || isEdit || !isInitialized) return;
    if (!allMasterFields || allMasterFields.length === 0) return;

    const categoryChanged = prevCategoryNodeIdRef.current !== categoryNodeId;
    prevCategoryNodeIdRef.current = categoryNodeId;

    // allMasterFields also changes when the star toggle refetches master fields for the SAME
    // category (e.g. un-starring a field). That must only clear the field's "default" badge, not
    // drop it from the modal -- so only run the drop logic below when the category itself
    // changed. Still pick up any newly-starred field so clicking the star back on adds it in.
    if (!categoryChanged) {
      setAddedFields((prevFields) => {
        const { newFields, newValues } = autoAddSelectionDefaultFields(prevFields, latestFieldValuesRef.current);
        if (newFields.length !== prevFields.length) {
          setDynamicFieldValues((prevValues) => ({ ...prevValues, ...newValues }));
        }
        return newFields;
      });
      return;
    }

    setAddedFields((prevFields) => {
      const latestValues = latestFieldValuesRef.current;
      const survivors = prevFields.filter((f) => {
        if (!f.IsAutoDefaultField) return true;
        const found = findFieldInMasterFields(f.FieldName, f.masterFieldId || f.Id);
        const stillDefault = !!(found?.field?.IsSelectionDefaultForCategoryNode || found?.field?.isSelectionDefaultForCategoryNode);
        if (stillDefault) return true;
        // Safe to drop only if nothing has touched the value since it was auto-added -- compare
        // against the field's own DefaultValue (not just blank), since a field whose master
        // definition carries a non-empty DefaultValue would otherwise never look "untouched" and
        // would incorrectly survive the category switch.
        const currentValue = latestValues[f.FieldName];
        const valueAtAutoAdd = f.DefaultValue || '';
        const untouchedSinceAutoAdd = currentValue === undefined || currentValue === null || currentValue === valueAtAutoAdd;
        return !untouchedSinceAutoAdd;
      });

      const droppedNames = prevFields
        .filter((f) => !survivors.some((s) => s.FieldName === f.FieldName))
        .map((f) => f.FieldName);

      const { newFields, newValues } = autoAddSelectionDefaultFields(survivors, latestValues);

      if (droppedNames.length > 0 || newFields.length !== survivors.length) {
        setDynamicFieldValues((prevValues) => {
          const next = { ...prevValues };
          droppedNames.forEach((n) => { delete next[n]; });
          return { ...next, ...newValues };
        });
      }

      return newFields;
    });
  }, [categoryNodeId, allMasterFields, isSelectionModule, isEdit, isInitialized]);

  const handleSelectMasterField = (fieldData) => {
    const exists = addedFields.some(
      (f) =>
        (f.FieldName || '').toLowerCase() === (fieldData.fieldName || '').toLowerCase() ||
        (f.masterFieldId || f.Id) === (fieldData.masterFieldId || fieldData.Id)
    );

    if (exists) {
      toast.error('This field is already added');
      return;
    }

    const newField = {
      Id: fieldData.masterFieldId || fieldData.Id || `master-${Date.now()}`,
      masterFieldId: fieldData.masterFieldId || fieldData.Id,
      FieldName: fieldData.fieldName || fieldData.FieldName,
      FieldLabel: fieldData.fieldLabel || fieldData.FieldLabel,
      HtmlInputType: (() => {
        const rawType = (
          fieldData.htmlInputType ||
          fieldData.HtmlInputType ||
          fieldData.value ||
          (fieldData.fieldTypeId || fieldData.FieldTypeId ? getHtmlInputType(fieldData.fieldTypeId || fieldData.FieldTypeId) : '') ||
          'text'
        ).toLowerCase();
        return (rawType === 'texteditor' || rawType === 'editor' || Number(fieldData.fieldTypeId || fieldData.FieldTypeId) === 11)
          ? 'editor'
          : rawType;
      })(),
      IsMendatory: fieldData.isMendatory || fieldData.IsMendatory || false,
      options: fieldData.options || fieldData.MasterFormFieldOptions || [],
      DefaultValue: fieldData.defaultValue || fieldData.DefaultValue || '',
      Placeholder: fieldData.placeholder || fieldData.Placeholder || '',
    };

    setAddedFields((prev) => [...prev, newField]);
    setDynamicFieldValues((prev) => ({
      ...prev,
      [newField.FieldName]: newField.DefaultValue || '',
    }));

    // Assign to step
    const assignedStep = targetStepForAdd || currentStep || (steps && steps[0]?.id) || 'other_info';
    const updatedLayout = { ...stepLayout };
    const stepList = [...(updatedLayout[assignedStep] || [])];
    if (!stepList.some((n) => n.toLowerCase() === newField.FieldName.toLowerCase())) {
      stepList.push(newField.FieldName);
    }
    updatedLayout[assignedStep] = stepList;

    const newPlacements = { ...fieldPlacements, [newField.FieldName]: 'bottom' };
    persistLayoutPreference(updatedLayout, newPlacements);

    saveFieldToStorage(newField.FieldName);

    const stepLabel = steps?.find((s) => s.id === assignedStep)?.label || assignedStep;
    toast.success(`"${newField.FieldLabel}" field added to ${stepLabel}`);

    if (onFieldAdded) {
      onFieldAdded();
    }
  };

  const handleDynamicFieldChange = (fieldName, value) => {
    setDynamicFieldValues((prev) => ({
      ...prev,
      [fieldName]: value,
    }));
  };

  const handleRemoveField = async (fieldName) => {
    const removedField = addedFields.find((f) => f.FieldName === fieldName);

    setAddedFields((prev) => prev.filter((f) => f.FieldName !== fieldName));
    setDynamicFieldValues((prev) => {
      const updated = { ...prev };
      delete updated[fieldName];
      return updated;
    });

    // Remove from step layout and placements
    const updatedLayout = { ...stepLayout };
    Object.keys(updatedLayout).forEach((stepKey) => {
      if (Array.isArray(updatedLayout[stepKey])) {
        updatedLayout[stepKey] = updatedLayout[stepKey].filter((n) => n.toLowerCase() !== fieldName.toLowerCase());
      }
    });

    const newPlacements = { ...fieldPlacements };
    delete newPlacements[fieldName];
    persistLayoutPreference(updatedLayout, newPlacements);

    removeFieldFromStorage(fieldName);
    toast.success("Field removed from form");

    // Deleting the field outright must also clear its per-Main-Category default flag -- unlike
    // the star toggle (which only unsets the default and leaves the field in the modal), a
    // direct delete removes both. Without this, MasterFormFieldCategoryNodeDefault keeps a stale
    // row and the field silently reappears (auto-added back) next time this category is opened.
    if (isSelectionModule && categoryNodeId) {
      const found = findFieldInMasterFields(fieldName, removedField?.masterFieldId || removedField?.Id);
      const isDefaultForThisCategory = !!(found?.field?.IsSelectionDefaultForCategoryNode || found?.field?.isSelectionDefaultForCategoryNode);
      if (isDefaultForThisCategory && found?.field?.Id) {
        try {
          const result = await setSelectionCategoryNodeDefaultField(found.field.Id, categoryNodeId, false);
          if (result.success) {
            fetchAllMasterFields();
          }
        } catch (error) {
          console.error('Error clearing default flag for removed field:', error);
        }
      }
    }
  };

  // Move a field by index within its step (matches Form Builder onMoveField)
  const handleMoveFieldByIndex = (stepId, fromIndex, toIndex, explicitKeysList = null) => {
    if (fromIndex === toIndex || fromIndex < 0) return;
    let currentNames = [];
    if (explicitKeysList && Array.isArray(explicitKeysList)) {
      currentNames = [...explicitKeysList];
    } else if (stepLayout[stepId] && stepLayout[stepId].length > 0) {
      currentNames = [...stepLayout[stepId]];
    } else {
      const currentFields = getFieldsForStep(stepId);
      currentNames = currentFields.map((f) => f.FieldName);
    }
    if (!currentNames[fromIndex]) return;

    if (toIndex < 0) toIndex = 0;
    if (toIndex >= currentNames.length) toIndex = currentNames.length - 1;

    const [moved] = currentNames.splice(fromIndex, 1);
    currentNames.splice(toIndex, 0, moved);

    if (stepId) {
      const updatedLayout = { ...stepLayout, [stepId]: currentNames };
      setStepLayout(updatedLayout);
      persistLayoutPreference(updatedLayout, fieldPlacements);
    } else {
      const reorderedFields = currentNames.map((name) =>
        addedFields.find((f) => (f.FieldName || '').toLowerCase() === name.toLowerCase())
      ).filter(Boolean);
      setAddedFields(reorderedFields);
      const updatedLayout = { ...stepLayout, default: currentNames };
      setStepLayout(updatedLayout);
      persistLayoutPreference(updatedLayout, fieldPlacements);
    }
    toast.success("Field order updated");
  };

  // Move a field up or down within its step
  const handleMoveField = (fieldName, direction, stepId) => {
    const currentFields = getFieldsForStep(stepId);
    const idx = currentFields.findIndex((f) => f.FieldName.toLowerCase() === fieldName.toLowerCase());
    if (idx === -1) return;
    const targetIdx = direction === 'up' ? idx - 1 : idx + 1;
    handleMoveFieldByIndex(stepId, idx, targetIdx);
  };

  const handleChangeFieldPlacement = (fieldName, placement) => {
    const newPlacements = { ...fieldPlacements, [fieldName]: placement };
    setFieldPlacements(newPlacements);
    persistLayoutPreference(stepLayout, newPlacements);
  };

  const handleToggleFieldDefault = async (field) => {
    if (togglingDefaultFieldName) return;
    if (!categoryNodeId) {
      toast.error("Choose a Main Category first — defaults are set per Main Category");
      return;
    }
    const found = findFieldInMasterFields(field.FieldName, field.masterFieldId || field.Id);
    if (!found) {
      toast.error("Could not find this field's master definition");
      return;
    }
    setTogglingDefaultFieldName(field.FieldName);
    try {
      const masterField = found.field;
      const nextValue = !(masterField.IsSelectionDefaultForCategoryNode || masterField.isSelectionDefaultForCategoryNode);
      const result = await setSelectionCategoryNodeDefaultField(masterField.Id || masterField.id, categoryNodeId, nextValue);
      if (result.success) {
        toast.success(nextValue ? `"${field.FieldLabel}" set as default for this Main Category` : `"${field.FieldLabel}" is no longer a default field for this Main Category`);
        await fetchAllMasterFields();
      } else {
        toast.error(result.message || "Failed to update default field");
      }
    } finally {
      setTogglingDefaultFieldName(null);
    }
  };

  // Helper to get fields for a step in configured order, with optional placement filter
  const getFieldsForStep = (stepId, sectionPlacement = null) => {
    if (!steps || steps.length === 0) return addedFields;
    const orderedNames = stepLayout[stepId] || [];
    const matched = [];
    orderedNames.forEach((name) => {
      const f = addedFields.find((field) => (field.FieldName || '').toLowerCase() === name.toLowerCase());
      if (f && !matched.some((m) => m.Id === f.Id)) {
        matched.push(f);
      }
    });

    // Unassigned fields fallback to 'other_info' or last step
    const isFallbackStep = stepId === 'other_info' || stepId === steps[steps.length - 1]?.id;
    if (isFallbackStep) {
      const allAssigned = new Set();
      Object.values(stepLayout).forEach((arr) => {
        if (Array.isArray(arr)) arr.forEach((n) => allAssigned.add(n.toLowerCase()));
      });
      addedFields.forEach((f) => {
        if (!allAssigned.has((f.FieldName || '').toLowerCase()) && !matched.some((m) => m.Id === f.Id)) {
          matched.push(f);
        }
      });
    }

    return matched;
  };

  // Map of all fields organized by step
  const fieldsByStep = useMemo(() => {
    if (!steps) return {};
    const res = {};
    steps.forEach((s) => {
      res[s.id] = getFieldsForStep(s.id);
    });
    return res;
  }, [steps, addedFields, stepLayout]);

  useImperativeHandle(ref, () => ({
    save: async (targetEntityId) => {
      try {
        const payloadFields = await Promise.all(
          addedFields.map(async (f) => {
            const val = dynamicFieldValues[f.FieldName];
            const fieldType = (f.HtmlInputType || 'text').toLowerCase();

            if (fieldType === 'attachments' || fieldType === 'file') {
              let filesArray = [];
              if (Array.isArray(val)) {
                filesArray = val;
              } else if (typeof val === 'string' && val.trim()) {
                try {
                  filesArray = JSON.parse(val);
                  if (!Array.isArray(filesArray)) filesArray = [];
                } catch (e) {
                  filesArray = val.split(',').map((s) => s.trim()).filter(Boolean);
                }
              }

              const newFilesToUpload = filesArray.filter((item) => item instanceof File);
              const existingItems = filesArray.filter((item) => !(item instanceof File));

              let newlyUploadedDocs = [];
              if (newFilesToUpload.length > 0) {
                const uploadRes = await uploadAttachments(
                  DocumentUploadFeature.ACTIVITY_ATTACHMENT,
                  newFilesToUpload
                );
                if (uploadRes.success && uploadRes.data) {
                  const nowIso = new Date().toISOString();
                  newlyUploadedDocs = uploadRes.data.map((item) => ({
                    fileName: item.fileName,
                    filePath: item.filePath,
                    fileExtension: item.fileExtension,
                    fileSizeKB: item.fileSizeKB || 0,
                    uploadedAt: nowIso,
                    uploadedBy: loggedInUserName,
                    uploadedByName: loggedInUserName,
                    UploadedByName: loggedInUserName
                  }));
                } else {
                  throw new Error(uploadRes.message || 'Failed to upload dynamic field attachments');
                }
              }

              const formattedExistingDocs = existingItems.map((item) => {
                if (typeof item === 'string') {
                  return {
                    fileName: item.split('/').pop(),
                    filePath: item,
                    fileExtension: '',
                    fileSizeKB: 0,
                    uploadedAt: new Date().toISOString(),
                    uploadedBy: loggedInUserName,
                    uploadedByName: loggedInUserName,
                    UploadedByName: loggedInUserName
                  };
                }
                return {
                  fileName: item.fileName || item.name || item.filePath?.split('/').pop() || 'Attachment',
                  filePath: item.filePath || item.path || item.FileUrl || '',
                  fileExtension: item.fileExtension || item.extension || '',
                  fileSizeKB: item.fileSizeKB || (item.size ? item.size / 1024 : 0),
                  uploadedAt: item.uploadedAt || item.UploadedAt || item.createdAt || item.CreatedAt || new Date().toISOString(),
                  uploadedBy: item.uploadedBy || item.UploadedBy || item.uploadedByName || item.UploadedByName || loggedInUserName,
                  uploadedByName: item.uploadedByName || item.UploadedByName || item.uploadedBy || item.UploadedBy || loggedInUserName,
                  UploadedByName: item.UploadedByName || item.uploadedByName || item.uploadedBy || item.UploadedBy || loggedInUserName
                };
              }).filter((item) => item.filePath || item.fileName);

              const finalDocsList = [...formattedExistingDocs, ...newlyUploadedDocs];

              return {
                masterFieldId: f.masterFieldId || f.Id,
                fieldName: f.FieldName,
                fieldValue: JSON.stringify(finalDocsList)
              };
            }

            return {
              masterFieldId: f.masterFieldId || f.Id,
              fieldName: f.FieldName,
              fieldValue: String(val ?? '')
            };
          })
        );

        const res = await saveEntityFieldValues({
          entityTypeCode,
          entityId: targetEntityId,
          fields: payloadFields
        }, companyId, userId);
        invalidateEntityDynamicFieldsCache(entityTypeCode, targetEntityId);
        return { ...res, resolvedFields: payloadFields };
      } catch (error) {
        console.error('Error saving dynamic fields:', error);
        throw error;
      }
    },
    openAddFieldModal: (stepId = null) => {
      setTargetStepForAdd(stepId || currentStep || null);
      setIsAddFieldModalOpen(true);
    },
    openReorderModal: (stepId = null) => {
      setReorderActiveStep(stepId || currentStep || null);
      setIsReorderModalOpen(true);
    },
    getStepFieldCount: (stepId) => {
      return (getFieldsForStep(stepId) || []).length;
    }
  }));

  const contextValue = {
    addedFields,
    dynamicFieldValues,
    fieldPlacements,
    handleDynamicFieldChange,
    handleRemoveField,
    handleMoveField,
    handleMoveFieldByIndex,
    handleChangeFieldPlacement,
    steps,
    currentStep,
    stepLayout,
    getFieldsForStep,
    fieldsByStep,
    openAddFieldModal: (stepId = null) => {
      setTargetStepForAdd(stepId || currentStep || null);
      setIsAddFieldModalOpen(true);
    },
    openReorderModal: (stepId = null) => {
      setReorderActiveStep(stepId || currentStep || null);
      setIsReorderModalOpen(true);
    },
    isSelectionModule,
    allowSetDefault,
    findFieldInMasterFields,
    handleToggleFieldDefault,
    togglingDefaultFieldName,
    autoAddAttachments,
    loggedInUserName,
    isDragAndDropEnabled
  };

  return (
    <EntityDynamicFieldsContext.Provider value={contextValue}>
      {children ? (
        children
      ) : (
        <>
          {addedFields.length > 0 ? (
            <>
              <SectionDivider title={sectionTitle} />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {addedFields.map((field, idx) => (
                  <RenderDynamicFieldItem
                    key={field.Id || field.FieldName}
                    field={field}
                    index={idx}
                    totalOnStep={addedFields.length}
                    stepId={null}
                    dynamicFieldValues={dynamicFieldValues}
                    handleDynamicFieldChange={handleDynamicFieldChange}
                    handleRemoveField={handleRemoveField}
                    handleMoveField={handleMoveField}
                    handleMoveFieldByIndex={handleMoveFieldByIndex}
                    isSelectionModule={isSelectionModule}
                    allowSetDefault={allowSetDefault}
                    findFieldInMasterFields={findFieldInMasterFields}
                    handleToggleFieldDefault={handleToggleFieldDefault}
                    togglingDefaultFieldName={togglingDefaultFieldName}
                    autoAddAttachments={autoAddAttachments}
                    loggedInUserName={loggedInUserName}
                    isDragAndDropEnabled={isDragAndDropEnabled}
                  />
                ))}
              </div>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center p-12 border border-dashed border-gray-300 dark:border-dark-600 rounded-xl bg-gray-55/30 dark:bg-dark-900/5 mt-4">
              <p className="text-sm text-gray-500 dark:text-dark-350 mb-4">No custom fields have been added yet.</p>
              <button
                type="button"
                onClick={() => {
                  setTargetStepForAdd(currentStep);
                  setIsAddFieldModalOpen(true);
                }}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors shadow-sm cursor-pointer"
              >
                <PlusCircleIcon className="size-4" />
                Add Field
              </button>
            </div>
          )}
        </>
      )}

      {/* Field Selection Modal */}
      {isAddFieldModalOpen && (
        <FieldSelectionModal
          isOpen={isAddFieldModalOpen}
          onClose={() => setIsAddFieldModalOpen(false)}
          masterFields={allMasterFields}
          masterFieldsLoading={masterFieldsLoading}
          masterFieldsError={masterFieldsError}
          onSelectField={handleSelectMasterField}
          onRefreshMasterFields={fetchAllMasterFields}
          fieldTypes={fieldTypes}
          isSelectionModuleMasterField={isSelectionModule}
          companyId={companyId}
          userId={userId}
          isFieldAdded={(fieldData) => {
            const newFieldNameLower = (fieldData.FieldName || fieldData.fieldName || '').toLowerCase();
            const newMasterFieldId = fieldData.Id || fieldData.masterFieldId;
            return addedFields.some((f) => {
              const fNameLower = (f.FieldName || f.fieldName || '').toLowerCase();
              const fMasterId = f.MasterFieldId || f.masterFieldId || f.Id;
              return (
                (newFieldNameLower && fNameLower === newFieldNameLower) ||
                (newMasterFieldId && fMasterId === newMasterFieldId)
              );
            });
          }}
        />
      )}

      {/* Reorder & Placement Modal */}
      {isReorderModalOpen && (
        <ReorderFieldsModal
          isOpen={isReorderModalOpen}
          onClose={() => setIsReorderModalOpen(false)}
          steps={steps}
          activeStepId={reorderActiveStep || currentStep}
          fields={fieldsByStep}
          fieldPlacements={fieldPlacements}
          onMoveField={handleMoveField}
          onChangeFieldPlacement={handleChangeFieldPlacement}
          onRemoveField={handleRemoveField}
        />
      )}
    </EntityDynamicFieldsContext.Provider>
  );
});

EntityDynamicFieldsProvider.displayName = 'EntityDynamicFieldsProvider';

// Step Fields Subcomponent
export function EntityDynamicStepFields({
  stepId,
  title = "Custom Fields",
  isOtherInfoTab = false,
  staticFields = [],
  enableDragAndDrop
}) {
  const context = useEntityDynamicFieldsContext();
  const [gridRef] = useAutoAnimate({ duration: 220, easing: 'ease-out' });

  const [dragFromIndex, setDragFromIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [dragPosition, setDragPosition] = useState(null); // 'before' | 'after'
  const [fieldDraggable, setFieldDraggable] = useState(true);
  const [hoveredIndex, setHoveredIndex] = useState(null);

  if (!context) {
    return null;
  }

  const {
    getFieldsForStep,
    stepLayout = {},
    dynamicFieldValues,
    handleDynamicFieldChange,
    handleRemoveField,
    handleMoveField,
    handleMoveFieldByIndex,
    steps,
    openAddFieldModal,
    isSelectionModule,
    allowSetDefault,
    findFieldInMasterFields,
    handleToggleFieldDefault,
    togglingDefaultFieldName,
    autoAddAttachments,
    loggedInUserName,
    isDragAndDropEnabled: contextDragEnabled
  } = context;

  const isDragAndDropEnabled =
    typeof enableDragAndDrop === 'boolean'
      ? enableDragAndDrop
      : (contextDragEnabled ?? false);

  const customFieldsOnThisStep = getFieldsForStep(stepId) || [];
  const currentStepLayout = stepLayout[stepId] || [];
  const stepObj = steps?.find((s) => s.id === stepId);
  const stepLabel = stepObj?.label || title;

  // Compute unified list of items (static fields + custom fields) based on stepLayout
  const unifiedItems = useMemo(() => {
    if (!staticFields || staticFields.length === 0) {
      return customFieldsOnThisStep.map((cf) => ({ ...cf, id: cf.FieldName, isCustom: true }));
    }

    const staticMap = new Map(staticFields.map((sf) => [sf.id, { ...sf, isStatic: true }]));
    const customMap = new Map(customFieldsOnThisStep.map((cf) => [cf.FieldName, { ...cf, id: cf.FieldName, isCustom: true }]));

    const result = [];
    const seen = new Set();

    // 1. Follow currentStepLayout
    currentStepLayout.forEach((key) => {
      if (staticMap.has(key) && !seen.has(key)) {
        result.push(staticMap.get(key));
        seen.add(key);
      } else if (customMap.has(key) && !seen.has(key)) {
        result.push(customMap.get(key));
        seen.add(key);
      }
    });

    // 2. Add remaining static fields
    staticFields.forEach((sf) => {
      if (!seen.has(sf.id)) {
        result.push({ ...sf, isStatic: true });
        seen.add(sf.id);
      }
    });

    // 3. Add remaining custom fields
    customFieldsOnThisStep.forEach((cf) => {
      if (!seen.has(cf.FieldName)) {
        result.push({ ...cf, id: cf.FieldName, isCustom: true });
        seen.add(cf.FieldName);
      }
    });

    return result;
  }, [staticFields, customFieldsOnThisStep, currentStepLayout]);

  // Drag prevention helper - identical to Form Builder InteractiveFormPreview.jsx
  const shouldPreventDrag = (e) => {
    const target = e.target;
    if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT') {
      return true;
    }
    const inputParent = target.closest('input, textarea, select, [contenteditable="true"], button');
    if (inputParent) {
      return true;
    }
    if (window.getSelection && window.getSelection().toString().length > 0) {
      return true;
    }
    if (target.contentEditable === 'true') {
      return true;
    }
    if (target.tagName === 'LABEL' || target.tagName === 'BUTTON') {
      return true;
    }
    if (target.hasAttribute('data-no-drag') || target.closest('[data-no-drag]')) {
      return true;
    }
    return false;
  };

  // Mouse down handler - disables card dragging when interacting with form inputs
  const handleMouseDown = (e) => {
    const target = e.target;
    const isInputElement = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT';
    const isInsideInput = target.closest('input, textarea, select, [contenteditable="true"], button');

    if (isInputElement || isInsideInput) {
      setFieldDraggable(false);

      const enableDragOnBlur = () => {
        setTimeout(() => setFieldDraggable(true), 50);
      };

      const inputElement = isInputElement ? target : isInsideInput;
      inputElement.addEventListener('blur', enableDragOnBlur, { once: true });

      setTimeout(() => setFieldDraggable(true), 2000);
    }
  };

  const handleDragStart = (index, e) => {
    if (shouldPreventDrag(e)) {
      e.preventDefault();
      return false;
    }
    setDragFromIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    try {
      e.dataTransfer.setData('text/plain', String(index));
    } catch {
      // Ignore dataTransfer issues on unsupported browsers
    }
  };

  const handleDragOver = (index, e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';

    if (dragFromIndex === null || dragFromIndex === index) {
      return;
    }

    const rect = e.currentTarget.getBoundingClientRect();
    const mouseY = e.clientY;
    const mouseX = e.clientX;
    const elementMiddleY = rect.top + rect.height / 2;
    const elementMiddleX = rect.left + rect.width / 2;

    const position = (mouseY < elementMiddleY || (Math.abs(mouseY - elementMiddleY) < 20 && mouseX < elementMiddleX))
      ? 'before'
      : 'after';

    setDragOverIndex(index);
    setDragPosition(position);
  };

  const handleDrop = (index, e) => {
    e.preventDefault();

    if (dragFromIndex === null || dragFromIndex === index) {
      setDragFromIndex(null);
      setDragOverIndex(null);
      setDragPosition(null);
      return;
    }

    let dropIndex = index;
    const rect = e.currentTarget.getBoundingClientRect();
    const mouseY = e.clientY;
    const mouseX = e.clientX;
    const elementMiddleY = rect.top + rect.height / 2;
    const elementMiddleX = rect.left + rect.width / 2;
    const position = (mouseY < elementMiddleY || (Math.abs(mouseY - elementMiddleY) < 20 && mouseX < elementMiddleX))
      ? 'before'
      : 'after';

    if (position === 'after') {
      dropIndex = index + 1;
    }

    if (dragFromIndex < dropIndex) {
      dropIndex = dropIndex - 1;
    }

    handleMoveFieldByIndex(stepId, dragFromIndex, dropIndex, unifiedItems.map((i) => i.id));

    setDragFromIndex(null);
    setDragOverIndex(null);
    setDragPosition(null);
  };

  const handleDragEnd = () => {
    setDragFromIndex(null);
    setDragOverIndex(null);
    setDragPosition(null);
  };

  const hasStaticFields = staticFields && staticFields.length > 0;

  if (unifiedItems.length === 0) {
    if (!isOtherInfoTab) {
      return null;
    }
    return (
      <div className="flex flex-col items-center justify-center p-12 border border-dashed border-gray-300 dark:border-dark-600 rounded-xl bg-gray-55/30 dark:bg-dark-900/5 mt-4">
        <p className="text-sm text-gray-500 dark:text-dark-350 mb-4 text-center">
          No custom fields currently on this step. Click &ldquo;Add Field&rdquo; above to add fields to this step.
        </p>
        <button
          type="button"
          onClick={() => openAddFieldModal(stepId)}
          className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors shadow-sm cursor-pointer"
        >
          <PlusCircleIcon className="size-4" />
          Add Field
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-3 pt-2">
      {!hasStaticFields && (
        <>
          <div className="flex items-center justify-between">
            <SectionDivider title={`${stepLabel} - Custom Fields`} />
          </div>

          <div className="flex items-center justify-between -mt-2 mb-2 px-1">
            <span className="text-xs font-semibold text-gray-500 dark:text-dark-400">
              {customFieldsOnThisStep.length} custom {customFieldsOnThisStep.length === 1 ? 'field' : 'fields'}
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => openAddFieldModal(stepId)}
                className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-950/30 border border-primary-200 dark:border-primary-800 rounded-lg transition-colors shadow-2xs cursor-pointer"
              >
                <PlusCircleIcon className="size-3.5" />
                Add Field
              </button>
            </div>
          </div>
        </>
      )}

      <div ref={gridRef} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {unifiedItems.map((item, idx) => {
          const isDragging = dragFromIndex === idx;
          const isTargetOver = dragOverIndex === idx && dragFromIndex !== null && dragFromIndex !== idx;
          const isHovered = hoveredIndex === idx;

          if (item.isCustom) {
            return (
              <RenderDynamicFieldItem
                key={item.Id || item.FieldName}
                field={item}
                index={idx}
                totalOnStep={unifiedItems.length}
                stepId={stepId}
                dynamicFieldValues={dynamicFieldValues}
                handleDynamicFieldChange={handleDynamicFieldChange}
                handleRemoveField={handleRemoveField}
                handleMoveField={handleMoveField}
                handleMoveFieldByIndex={(sId, from, to) =>
                  handleMoveFieldByIndex(sId, from, to, unifiedItems.map((i) => i.id))
                }
                dragFromIndex={dragFromIndex}
                dragOverIndex={dragOverIndex}
                dragPosition={dragPosition}
                fieldDraggable={fieldDraggable}
                handleMouseDown={handleMouseDown}
                handleDragStart={handleDragStart}
                handleDragOver={handleDragOver}
                handleDrop={handleDrop}
                handleDragEnd={handleDragEnd}
                isHovered={isHovered}
                setHoveredIndex={setHoveredIndex}
                isSelectionModule={isSelectionModule}
                allowSetDefault={allowSetDefault}
                findFieldInMasterFields={findFieldInMasterFields}
                handleToggleFieldDefault={handleToggleFieldDefault}
                togglingDefaultFieldName={togglingDefaultFieldName}
                autoAddAttachments={autoAddAttachments}
                loggedInUserName={loggedInUserName}
                isDragAndDropEnabled={isDragAndDropEnabled}
              />
            );
          }

          // Static field card (Drop target for custom fields; no direct move buttons)
          return (
            <div
              key={item.id}
              onDragOver={isDragAndDropEnabled ? (e) => handleDragOver(idx, e) : undefined}
              onDrop={isDragAndDropEnabled ? (e) => handleDrop(idx, e) : undefined}
              className={clsx(
                item.fullWidth ? "col-span-1 sm:col-span-2" : "col-span-1",
                "relative p-3.5 border rounded-xl transition-all duration-200 bg-white dark:bg-dark-850",
                isDragAndDropEnabled && isTargetOver
                  ? "border-primary-500 ring-2 ring-primary-500/20 shadow-md"
                  : "border-gray-200 dark:border-dark-700 shadow-2xs hover:border-gray-300 dark:hover:border-dark-600"
              )}
            >
              {/* Drop Indicator Bar from Form Builder */}
              {isDragAndDropEnabled && isTargetOver && (
                <div
                  className={clsx(
                    "absolute inset-x-0 h-1.5 bg-primary-600 rounded-full z-30 shadow-md animate-pulse pointer-events-none",
                    dragPosition === 'before' ? "-top-1.5" : "-bottom-1.5"
                  )}
                />
              )}

              {item.render ? item.render() : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Default export wrapper for backward compatibility
export const EntityDynamicFieldsForm = forwardRef((props, ref) => {
  return <EntityDynamicFieldsProvider ref={ref} {...props} />;
});

EntityDynamicFieldsForm.displayName = 'EntityDynamicFieldsForm';
